import { OperatingSystem } from '@capacitor/device';
import { MembershipPlan } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';
import { User } from 'shared/types';
import { ENV } from 'shared/ENV';
import { Capacitor } from '@capacitor/core';
import { getDeviceInfo } from 'state/localStorageTyped';
import { AppStoreLinks, WATCHDUTY_SCHEME } from '../../../../constants';

const { VITE_BASE_WEB_URL } = ENV;

export const getSupportUsDeepLink = (
  plan: MembershipPlan,
  os?: OperatingSystem,
): string => {
  if (os === 'ios') {
    // Web on iPhone
    const paywallLink = getPaywallLink({
      focusedPlan: plan,
      source: 'deep_link_ios',
    });
    return `${WATCHDUTY_SCHEME}app.watchduty.org${paywallLink.pathname}${paywallLink.search || ''}`;
  }
  if (os === 'mac') {
    // Web on Mac or iPad
    return 'https://apps.apple.com/account/subscriptions';
  }
  // We don't need to deep-link on Android since we do payments on the web there, so always default to iOS link
  return AppStoreLinks.ios;
};

export const buildLinkParams = (args: {
  plan: MembershipPlan;
  user: User | null;
  isSupporter: boolean;
  clientToken: string;
  isMembershipUpgrade: boolean;
}): string => {
  const { plan, user, isSupporter, clientToken, isMembershipUpgrade } = args;
  const params = new URLSearchParams({ plan });
  if (user) {
    // Do not use encodeURIComponent - URLSearchParams encodes values on serialization;
    // pre-encoding causes double-encoding.
    params.append('email', user.email);
    user.firstName && params.append('firstName', user.firstName);
    user.lastName && params.append('lastName', user.lastName);
  }
  if (user && isSupporter) {
    params.append('userId', user.id.toString());
    params.append('verificationToken', clientToken);
  } else {
    params.append('clientToken', clientToken);
  }
  if (isMembershipUpgrade) {
    params.append('upgrade', 'true');
  }
  return params.toString();
};

export const getContinueOnWeb = (args: {
  isMembershipUpgrade: boolean;
  isIosSubscription: boolean;
}): boolean => {
  const { isMembershipUpgrade, isIosSubscription } = args;
  const osPlatform = Capacitor.getPlatform();

  const isIOS = osPlatform === 'ios';
  const isAndroid = osPlatform === 'android';

  const isIosButNotIosSubscription = isIOS && !isIosSubscription;
  const isIosSubscriptionButNotIos = isIosSubscription && !isIOS;

  return (
    isAndroid ||
    (isMembershipUpgrade &&
      (isIosButNotIosSubscription || isIosSubscriptionButNotIos))
  );
};

export const getBecomeAMemberLink = (args: {
  clientToken: string;
  isSupporter: boolean;
  user: User | null;
  plan: MembershipPlan;
  isMembershipUpgrade: boolean;
  isIosSubscription: boolean;
}): string => {
  const {
    clientToken,
    isSupporter,
    user,
    plan,
    isMembershipUpgrade,
    isIosSubscription,
  } = args;

  const capPlatform = Capacitor.getPlatform();

  const deviceInfo = getDeviceInfo();
  const osSystem = deviceInfo?.operatingSystem;

  const params = buildLinkParams({
    plan,
    user,
    isSupporter,
    clientToken,
    isMembershipUpgrade,
  });

  if (capPlatform === 'ios') {
    if (isMembershipUpgrade && !isIosSubscription) {
      return `${VITE_BASE_WEB_URL}membership/account_info?${params}`;
    }
    return '/membership/checkout';
  }
  if (isMembershipUpgrade && isIosSubscription) {
    return getSupportUsDeepLink(plan, osSystem);
  }
  if (capPlatform !== 'android') {
    // Web
    return '/membership/account_info';
  }
  // Android
  return `${VITE_BASE_WEB_URL}membership/account_info?${params}`;
};
